/**
 * Shared table utility components
 * Used across encoders, persistent-streams, and other table views
 */

import { ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Re-export shared cell components for convenience
export * from './tables/cells';
import type { EncoderWSSRecord } from '@/client/store/encoderWSSStore';
import { ENCODER_CONFIG } from '@shared/types';

/**
 * Sortable column header for TanStack Table
 */
export function SortableHeader({ column, children }: { column: any; children: React.ReactNode }) {
  const sorted = column.getIsSorted();
  return (
    <button
      className="flex items-center gap-1 hover:text-foreground"
      onClick={() => column.toggleSorting(sorted === 'asc')}
    >
      {children}
      {sorted === 'asc' ? (
        <ArrowUp className="h-3 w-3" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="h-3 w-3" />
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-50" />
      )}
    </button>
  );
}

/**
 * Flapping state indicator for encoders
 */
export function FlappingIndicator({ state }: { state: 'normal' | 'warning' | 'alert' | undefined }) {
  if (!state || state === 'normal') return null;
  if (state === 'alert') {
    return (
      <span title="Encoder flapping (alert)">
        <AlertCircle className="h-4 w-4 text-red-500" />
      </span>
    );
  }
  return (
    <span title="Encoder flapping (warning)">
      <AlertTriangle className="h-4 w-4 text-yellow-500" />
    </span>
  );
}

interface LiveStatusProps {
  /** Real-time WSS data (if available) */
  wss: EncoderWSSRecord | null;
  /** Database status: 0=Offline, 1=Online, 2=Streaming */
  dbStatus: number | null;
}

/**
 * Live status indicator with WSS real-time data and DB fallback
 */
export function LiveStatus({ wss, dbStatus }: LiveStatusProps) {
  // If we have WSS data, use it (more real-time)
  if (wss) {
    return (
      <div className="flex items-center gap-2">
        <span className={wss.online ? 'text-green-600' : 'opacity-20'}>
          {wss.online ? 'Online' : 'Offline'}
        </span>
        <FlappingIndicator state={wss.state} />
      </div>
    );
  }

  // Fall back to DB status
  if (dbStatus === null) return <span>-</span>;
  switch (dbStatus) {
    case 0: return <span className="opacity-20">Offline</span>;
    case 1: return <span className="text-green-600">Online</span>;
    case 2: return <span>Streaming</span>;
    default: return <span>Unknown ({dbStatus})</span>;
  }
}

/** Staleness thresholds from shared config */
const { STALE_WARNING_MS, STALE_ALERT_MS } = ENCODER_CONFIG;

/**
 * Check how stale a lastCommunication timestamp is
 */
export function getStaleness(lastCommunication: string | null): 'fresh' | 'warning' | 'stale' {
  if (!lastCommunication) return 'stale';
  const diffMs = Date.now() - new Date(lastCommunication).getTime();
  if (diffMs > STALE_ALERT_MS) return 'stale';
  if (diffMs > STALE_WARNING_MS) return 'warning';
  return 'fresh';
}

interface LastCommunicationProps {
  /** ISO date string of last communication */
  lastCommunication: string | null;
}

/**
 * Last communication display
 * Shows relative time, muted if old
 * - Recent (< 1h): normal text
 * - Older: muted text (no icons - lastComm only updates on connect, so old is normal)
 */
export function LastCommunication({ lastCommunication }: LastCommunicationProps) {
  if (!lastCommunication) return <span className="text-muted-foreground">-</span>;

  const date = new Date(lastCommunication);
  const staleness = getStaleness(lastCommunication);
  const fullDate = date.toLocaleString();
  const relativeTime = formatDistanceToNow(date, { addSuffix: true });

  // Relative time, muted if old. Tooltip shows full timestamp.
  return (
    <span
      className={staleness === 'stale' ? 'text-muted-foreground' : ''}
      title={fullDate}
    >
      {relativeTime}
    </span>
  );
}
